import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getSystemConfig } from "../../../application/selectors/auth";
import ConsultationApi from "../../../infrastructure/api/consultation";
import PanelInfo from "../../components/shared/panel-info";

const mobileRegex = /^09\d{9}$/;

const MAIN_SITE_URL = "https://solarent.ir";

const normalizeMobile = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (d.startsWith("989") && d.length === 12) return `0${d.slice(2)}`;
  if (d.startsWith("9") && d.length === 10 && !d.startsWith("09")) return `0${d}`;
  return d;
};

const PublicConsultationPage = () => {
  const theme = useTheme();
  const { data: configs } = useSelector(getSystemConfig);
  const brandTitle = configs?.panelTitle || "سولارنت";

  const [mobile, setMobile] = useState("");
  const [fullName, setFullName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const norm = normalizeMobile(mobile);
    if (!mobileRegex.test(norm)) {
      toast.error("شماره موبایل معتبر وارد کنید (۱۱ رقم با ۰۹)");
      return;
    }
    try {
      setLoading(true);
      const res = await ConsultationApi.submitPublic({
        mobile: norm,
        fullName: fullName || undefined,
        message: message || undefined,
        source: "public-web",
      });
      if (res?.isSuccess) {
        setDone(true);
        toast.success(res.message || "ثبت شد");
      } else {
        toast.error(res?.message || "خطا در ثبت");
      }
    } catch (err) {
      toast.error("خطا در ارتباط با سرور");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: "100vh",
        py: { xs: 2, sm: 4 },
        px: 2,
        background: `linear-gradient(165deg, ${alpha(
          theme.palette.primary.main,
          0.12
        )} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 45%, ${alpha(
          theme.palette.primary.light,
          0.06
        )} 100%)`,
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            mx: "auto",
            maxWidth: 720,
            overflow: "hidden",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 12px 40px rgba(25, 118, 210, 0.12)",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 4,
              textAlign: "center",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "primary.contrastText",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 88,
                height: 88,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.95)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                mb: 2,
                p: 1.25,
              }}
            >
              <PanelInfo size={56} />
            </Box>
            <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
              درخواست مشاوره
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.95, fontWeight: 500 }}>
              {brandTitle}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.88, mt: 1.5, maxWidth: 420, mx: "auto" }}>
              پلتفرم نیروگاه‌های خورشیدی خانگی؛ همکاران ما برای راهنمایی با شما تماس می‌گیرند.
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            {done ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                درخواست شما با موفقیت ثبت شد. همکاران ما به زودی با شما تماس می‌گیرند.
                <Box sx={{ mt: 1.5 }}>
                  <Button component={Link} to="/auth/login" variant="outlined" size="small">
                    ورود به سامانه
                  </Button>
                </Box>
              </Alert>
            ) : (
              <Box component="form" onSubmit={onSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <SupportAgentIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary">
                        بدون نیاز به ورود، فرم زیر را تکمیل کنید.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="شماره موبایل"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder="09123456789"
                      variant="outlined"
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="نام (اختیاری)"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="متن درخواست (اختیاری)"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      multiline
                      minRows={3}
                      variant="outlined"
                      size="small"
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="medium"
                      fullWidth
                      disabled={loading}
                      sx={{
                        py: 1.1,
                        borderRadius: 2,
                        fontWeight: 600,
                        boxShadow: "0 8px 24px rgba(25, 118, 210, 0.35)",
                      }}
                    >
                      {loading ? "در حال ارسال…" : "ثبت درخواست مشاوره"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mx: "auto",
            maxWidth: 720,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.85),
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary" display="block">
                قبلاً در سولارنت حساب دارید؟
              </Typography>
              <Button component={Link} to="/auth/login" variant="text" color="primary" size="small">
                ورود به سامانه
              </Button>
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: "none", sm: "block" }, borderColor: "divider" }}
            />
            <Divider sx={{ display: { xs: "block", sm: "none" }, width: "100%", maxWidth: 280 }} />
            <Button
              component="a"
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              color="inherit"
              size="small"
            >
              بازگشت به صفحه اصلی
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicConsultationPage;
